import { ASK_THE_SUPERADMIN, plainWords, technical } from './plain';
import { denialFor, type Denial } from './denials';

/**
 * Every way a call can come back not-ok, as one shape a screen can render.
 *
 * Three services answer through one door and their refusals differ:
 *
 *   the gateway   403 `{ error: 'denied', reason, detail }`      — a policy decision
 *                 401 `{ error: 'unauthenticated' }`             — sign in
 *                 404 `{ error: 'not_found' }`                   — a path off the allow-list
 *                 503 `{ error: 'misconfigured', detail }`       — a deployment gap
 *   work-api      4xx `{ error: '<code>', detail? }`             — a refusal with its remedy in prose
 *   the network   —                                              — offline
 *
 * And some refusals are 200s: a lost claim race, a `refused` routing
 * outcome, an unanswered chat turn. Those are states a screen renders from
 * the body, and never reach this module. Nothing here composes a remedy:
 * the sentence in `detail` was written to be shown, and is shown as it came.
 */
export type Failure =
  | { kind: 'unauthenticated' }
  | { kind: 'denied'; reason: string; detail: string; denial: Denial; resumesAt?: string }
  | { kind: 'not_found'; path: string; /** No page of the app has this address, as against an API path off the list. */ page?: true }
  | { kind: 'refused'; status: number; code: string; detail: string }
  | { kind: 'misconfigured'; detail: string; code?: string }
  | { kind: 'server'; status: number }
  | { kind: 'offline' };

export class ApiError extends Error {
  constructor(readonly failure: Failure) {
    super(headline(failure));
    this.name = 'ApiError';
  }
}

export function isFailure(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Any thrown thing, as a failure a screen can render. Unknown errors are the server's. */
export function failureOf(error: unknown): Failure {
  return error instanceof ApiError ? error.failure : { kind: 'server', status: 0 };
}

export async function failureFrom(response: Response, path: string): Promise<Failure> {
  const body = (await response.json().catch(() => null)) as
    | { error?: string; reason?: string; detail?: string; resumesAt?: string }
    | null;
  if (response.status === 401) return { kind: 'unauthenticated' };
  if (response.status === 403 && body?.error === 'denied' && body.reason) {
    return {
      kind: 'denied',
      reason: body.reason,
      detail: body.detail ?? '',
      denial: denialFor(body.reason),
      // Human pace: when the refusal lifts, from the counter that refused.
      ...(typeof body.resumesAt === 'string' ? { resumesAt: body.resumesAt } : {}),
    };
  }
  if (response.status === 404 && body?.error === 'not_found') return { kind: 'not_found', path };
  if (response.status === 503 && body?.error === 'misconfigured') {
    return { kind: 'misconfigured', detail: body.detail ?? '' };
  }
  // A 503 that names itself — `no_dispatcher`, `sign_in_unconfigured`,
  // `markets_unreadable` — is a service saying what is missing, in words
  // written to be shown. Flattening it to "something went wrong" would
  // throw away the one sentence that says what to do.
  if (response.status === 503 && body?.error && body.detail) {
    return { kind: 'misconfigured', detail: body.detail, code: body.error };
  }
  if (response.status >= 500) return { kind: 'server', status: response.status };
  return {
    kind: 'refused',
    status: response.status,
    code: body?.error ?? 'refused',
    detail: body?.detail ?? '',
  };
}

/** The line a person reads first. */
export function headline(f: Failure, tech: boolean = technicalReader): string {
  const line = headlineOf(f);
  if (tech) return line;
  // A heading that only makes sense to whoever runs the platform — a code
  // turned into words, a deployment's own parts — is said as what it means
  // to the person: it is not switched on yet.
  if ((f.kind === 'refused' || f.kind === 'misconfigured') && (f.kind === 'misconfigured' || !(f.code in KNOWN_TITLES) || technical(line))) {
    return f.kind === 'misconfigured' ? 'This is not switched on yet' : 'That could not be done';
  }
  return plainWords(line);
}

function headlineOf(f: Failure): string {
  switch (f.kind) {
    case 'unauthenticated':
      return 'Your session ended. Sign in again to carry on.';
    case 'denied':
      return f.denial.title;
    case 'not_found':
      return f.page ? 'There is no page at this address.' : 'That is not something this interface can ask for.';
    case 'refused':
      return refusedTitle(f.code);
    case 'misconfigured':
      return f.code ? refusedTitle(f.code) : 'This deployment is missing something.';
    case 'server':
      return 'Something went wrong on our side.';
    case 'offline':
      return 'You are offline.';
  }
}

/** The line underneath: what to do about it. From the platform where it wrote one. */
/**
 * One line for a form's own error slot: the platform's sentence when it
 * wrote one — which says what to change — and the heading when it did not.
 * A heading alone ("Bad sequence") tells a person something was wrong and
 * not what.
 */
export function said(f: Failure): string {
  return remedy(f) || headline(f);
}

/**
 * Who is reading, for the helpers that have no component to ask: set by the
 * shell from the signed-in person, and false until then, so a message that
 * carries the platform's internals is shown only to a superadmin.
 */
let technicalReader = false;
export function setTechnicalReader(superadmin: boolean): void {
  technicalReader = superadmin;
}

export function remedy(f: Failure, tech: boolean = technicalReader): string {
  const line = remedyOf(f);
  if (tech) return line;
  switch (f.kind) {
    case 'denied': {
      const body = plainWords(f.denial.body);
      return technical(body) ? ASK_THE_SUPERADMIN : body;
    }
    case 'refused':
      // Its citations go; what is left is shown only if it names nothing internal.
      return f.detail && technical(plainWords(f.detail)) ? ASK_THE_SUPERADMIN : plainWords(f.detail);
    case 'misconfigured':
      return ASK_THE_SUPERADMIN;
    case 'not_found':
      return 'That page is not one you can open. Use the menu to find your way back.';
    default:
      return line;
  }
}

function remedyOf(f: Failure): string {
  switch (f.kind) {
    case 'denied':
      return f.detail ? `${f.denial.body} The gateway said: ${f.detail}` : f.denial.body;
    case 'refused':
      return f.detail;
    case 'misconfigured':
      return f.detail;
    case 'not_found':
      if (f.page) return `Nothing in the app lives at ${f.path}. Check the address, or use the menu to find the page.`;
      return `${f.path} is not on the list of paths a person may reach. The list is the platform's, and a screen asking for something off it is a screen that is wrong.`;
    case 'server':
      return 'It has been logged. Nothing you did was saved, so it is safe to try again.';
    case 'offline':
      return 'Nothing you typed is lost; try again when the connection is back.';
    case 'unauthenticated':
      return '';
  }
}

/**
 * A work-api refusal code, as a title. The remedy is the platform's
 * `detail`; this is only the heading over it, so a code without a heading
 * still reads as a sentence and not as `snake_case`.
 */
function refusedTitle(code: string): string {
  return KNOWN_TITLES[code] ?? code.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

const KNOWN_TITLES: Record<string, string> = {
    missing_field: 'Something the platform needed was not sent',
    bad_context: 'The chat can only be told what you are looking at, not what it says',
    no_session: 'That conversation is not open',
    bad_state: 'That is not a state this list knows',
    bad_rule: 'That window cannot be a rule',
    bad_duration: 'That is not a meeting length',
    no_such_task: 'That task is not here',
    no_such_mailbox: 'That mailbox is not here',
    nothing_to_brief: 'Nothing to brief from yet',
    no_thread: 'That conversation is not here',
    no_department: 'That has no department to run under',
    not_the_claimant: 'Somebody else has this',
    not_your_department: 'Not one of your departments',
    sign_in_unconfigured: 'Sign-in is not set up on this deployment',
    person_required: 'A person has to ask for this',
    wrong_side: 'That is the other department’s to do',
    wrong_state: 'That is not in a state where this applies',
    not_visible: 'That is not visible from here',
    promotion_needs_a_person: 'A person has to approve this',
    // Sending (S2, amendment 0002) and outreach (proposal 0007).
    dual_control_invalid: 'Nobody has said yes to this one yet',
    already_claimed: 'This has gone already',
    recipient_suppressed: 'Somebody on this asked us not to write again',
    no_dispatcher: 'Nothing here can send mail yet',
    bad_outcome: 'That is not a choice for a draft',
    body_blocked: 'The scanner held this text back',
    body_required: 'There is nothing to send',
    no_draft: 'That draft is not here',
    not_shared: 'That is not a shared mailbox',
    not_a_member: 'You are not on that mailbox',
    bad_address: 'That is not an email address',
    markets_unreadable: 'The list of countries cannot be read',
    // Prospecting (ADR-0064) and connections (ADR-0083, ADR-0085).
    no_prospecting_assignment: 'Nobody here is allowed to look for restaurants yet',
    twin_cannot_prospect: 'A twin does not look for restaurants',
    no_such_prospect: 'That restaurant is not on the list',
    bad_prospect: 'That line could not be read as a restaurant',
    wrong_stage: 'That restaurant is not at that step',
    market_not_declared: 'That country is not one we write to',
    market_closed: 'Writing to that country is not open',
    bad_market: 'That is not a country on the list',
    bad_query: 'Say what to look for in one line',
    bad_rows: 'The list could not be read',
    bad_stage: 'That is not a step on the list',
    no_such_connection: 'That is not a connection',
    key_required: 'Paste the key',
    bad_key: 'That does not look like a key',
    store_unconfigured: 'This deployment cannot reach where keys are kept',
    beyond_term: 'That is longer than these tools may be granted for',
    bad_term: 'A term is between 1 and 365 days',
    bad_level: 'That is not a level a term is set for',
    already_there: 'It is already set to that',
    // Campaigns (ADR-0079, ADR-0086).
    no_such_campaign: 'That campaign is not here',
    bad_campaign: 'That campaign cannot be drawn up as it is',
    not_the_approver: 'Only the department’s lead or a superadmin can do this',
    no_declaration: 'Nobody is declared to send from the shared box yet',
    no_watched_send: 'A person has to send one first and watch it arrive',
    from_name_unconfirmed: 'Say that the name held',
    window_over: 'That campaign’s dates have passed',
    not_inbound: 'Only somebody writing to us can complain',
    cannot_compose: 'That message cannot be put together from its record',
    // What a vendor may cost (ADR-0095).
    allowance_reached: 'What is free here is used',
    plan_ended: 'The plan with this vendor has ended',
    vendor_paused: 'This vendor is paused',
    bad_allowance: 'That line cannot be set as it is',
    bad_plan: 'That plan cannot be recorded as it is',
    unknown_vendor: 'That is not a vendor the platform reaches',
    not_paused: 'That vendor is not paused',
    already_paused: 'That vendor is already paused',
    not_for_sale: 'That is the vendor’s own limit: nothing can be bought past it',
    not_in_customerio: 'Customer.io has not been told about them yet',
    gateway_unconfigured: 'This deployment cannot reach the gateway',
    // Lifecycle (ADR-0082, ADR-0087).
    no_lifecycle_grant: 'Nobody here holds Customer.io yet',
    no_lifecycle_basis: 'Nobody is told to Customer.io without a basis on record',
    bad_evidence: 'That is not evidence of their say-so',
    bad_lifecycle: 'That cannot be recorded as it is',
    no_such_contact: 'That person is not on the lifecycle list',
    already_withdrawn: 'That was withdrawn already',
    not_an_event: 'That is not something Customer.io is told about',
    hook_only: 'Only Customer.io’s own reports come in that way',
    bad_events: 'That report could not be read',
};
